<template>
  <div class="friendly-model-selector" data-testid="friendly-model-selector">
    <p class="step-question">{{ $t('models_and_train_configs.friendly_what_should_ai_do') }}</p>

    <div class="goal-cards" role="list">
      <button
        v-for="goal in goals"
        :key="goal.id"
        type="button"
        class="goal-card"
        :class="{ selected: selectedGoal === goal.id, disabled: disabled }"
        :disabled="disabled"
        :data-testid="'goal-' + goal.id"
        role="listitem"
        @click="selectGoal(goal.id)"
      >
        <v-icon class="goal-icon" size="28">{{ goal.icon }}</v-icon>
        <span class="goal-title">{{ goal.title }}</span>
        <span class="goal-example">{{ goal.example }}</span>
      </button>
    </div>

    <div
      v-if="selectedGoal === 'bounding_box'"
      class="selector-step"
      data-testid="target-device"
    >
      <p class="step-question">{{ $t('models_and_train_configs.friendly_where_will_it_run') }}</p>
      <div class="goal-cards goal-cards--targets" role="list">
        <button
          v-for="target in targets"
          :key="target.id"
          type="button"
          class="goal-card"
          :class="{ selected: selectedTarget === target.id, disabled: disabled }"
          :disabled="disabled"
          :data-testid="'target-' + target.id"
          role="listitem"
          @click="selectTarget(target.id)"
        >
          <v-icon class="goal-icon" size="24">{{ target.icon }}</v-icon>
          <span class="goal-title">{{ target.title }}</span>
          <span class="goal-example">{{ target.example }}</span>
        </button>
      </div>
    </div>

    <div
      v-if="selectedGoal === 'classification'"
      class="selector-step"
      data-testid="classification-style"
    >
      <p class="step-question">{{ $t('models_and_train_configs.friendly_classification_style') }}</p>
      <div class="goal-cards goal-cards--narrow" role="list">
        <button
          type="button"
          class="goal-card"
          :class="{ selected: classificationStyle === 'simple', disabled: disabled }"
          :disabled="disabled"
          data-testid="class-style-simple"
          role="listitem"
          @click="selectClassificationStyle('simple')"
        >
          <v-icon class="goal-icon" size="24">fa-solid fa-tags</v-icon>
          <span class="goal-title">{{ $t('models_and_train_configs.friendly_class_simple_title') }}</span>
          <span class="goal-example">{{ $t('models_and_train_configs.friendly_class_simple_example') }}</span>
        </button>
        <button
          type="button"
          class="goal-card"
          :class="{ selected: classificationStyle === 'arcface', disabled: disabled }"
          :disabled="disabled"
          data-testid="class-style-arcface"
          role="listitem"
          @click="selectClassificationStyle('arcface')"
        >
          <v-icon class="goal-icon" size="24">fa-solid fa-fingerprint</v-icon>
          <span class="goal-title">{{ $t('models_and_train_configs.friendly_class_arcface_title') }}</span>
          <span class="goal-example">{{ $t('models_and_train_configs.friendly_class_arcface_example') }}</span>
        </button>
      </div>
    </div>

    <div
      v-if="summaryVisible"
      class="selection-summary"
      data-testid="model-selection-summary"
    >
      <div class="summary-text">
        <strong>{{ $t('models_and_train_configs.friendly_you_chose') }}</strong>
        {{ summaryTitle }}
        <span class="summary-model">({{ selectedModelDisplayName }})</span>
      </div>
      <p v-if="summaryHint" class="summary-hint">{{ summaryHint }}</p>
    </div>

    <div class="all-models-toggle">
      <button
        type="button"
        class="linkish"
        :disabled="disabled"
        data-testid="show-all-models"
        @click="showAllModels = !showAllModels"
      >
        {{ showAllModels
          ? $t('models_and_train_configs.friendly_hide_all_models')
          : $t('models_and_train_configs.friendly_show_all_models') }}
      </button>
    </div>

    <div v-show="showAllModels" class="all-models" data-testid="all-models-list">
      <p class="all-models-help">{{ $t('models_and_train_configs.friendly_all_models_help') }}</p>
      <model-button-overview
        :disabled="disabled"
        :selected_model_type="selected_model_type"
        :model_buttons="model_buttons"
        @clicked="onChipClicked"
      />
    </div>
  </div>
</template>

<script>
import ModelButtonOverview from "@/components/train_configs/ModelButtonOverview.vue";
import { BOXIFY_TARGET_PRESETS } from "@/utils/boxifyTargetPresets";

/** Recommended backends for the guided path (must exist in train_parameters.json). */
const RECOMMENDED = {
  bounding_box: 'boxify',
  classification_simple: 'convnextv2_tiny',
  classification_arcface: 'convnextv2_tiny',
  ocr: 'paddleocr',
};

export default {
  name: "FriendlyModelSelector",
  components: { ModelButtonOverview },
  props: {
    model_buttons: { required: true },
    selected_model_type: { required: true },
    target_device: { default: '' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedGoal: null,
      selectedTarget: null,
      classificationStyle: null,
      showAllModels: false,
    };
  },
  computed: {
    goals() {
      return [
        {
          id: 'bounding_box',
          icon: 'fa-sharp fa-light fa-boxes-stacked',
          title: this.$t('models_and_train_configs.friendly_goal_boxes_title'),
          example: this.$t('models_and_train_configs.friendly_goal_boxes_example'),
        },
        {
          id: 'classification',
          icon: 'fa-solid fa-image',
          title: this.$t('models_and_train_configs.friendly_goal_classify_title'),
          example: this.$t('models_and_train_configs.friendly_goal_classify_example'),
        },
        {
          id: 'ocr',
          icon: 'fa-solid fa-font',
          title: this.$t('models_and_train_configs.friendly_goal_ocr_title'),
          example: this.$t('models_and_train_configs.friendly_goal_ocr_example'),
        },
      ];
    },
    targets() {
      return [
        {
          id: 'phone',
          icon: 'fa-solid fa-mobile-screen',
          title: this.$t('models_and_train_configs.friendly_target_phone_title'),
          example: this.$t('models_and_train_configs.friendly_target_phone_example'),
        },
        {
          id: 'edge',
          icon: 'fa-solid fa-microchip',
          title: this.$t('models_and_train_configs.friendly_target_edge_title'),
          example: this.$t('models_and_train_configs.friendly_target_edge_example'),
        },
        {
          id: 'guidance',
          icon: 'fa-solid fa-binoculars',
          title: this.$t('models_and_train_configs.friendly_target_guidance_title'),
          example: this.$t('models_and_train_configs.friendly_target_guidance_example'),
        },
        {
          id: 'gpu',
          icon: 'fa-solid fa-server',
          title: this.$t('models_and_train_configs.friendly_target_gpu_title'),
          example: this.$t('models_and_train_configs.friendly_target_gpu_example'),
        },
      ];
    },
    selectedModel() {
      if (!this.selected_model_type) {
        return null;
      }
      return this.model_buttons.find((b) => b.id === this.selected_model_type) || null;
    },
    selectedModelDisplayName() {
      return this.selectedModel ? this.selectedModel.name : '';
    },
    summaryVisible() {
      return !!this.selectedModel;
    },
    summaryTitle() {
      if (this.selectedGoal === 'bounding_box' && this.selectedTarget) {
        return this.$t(`models_and_train_configs.friendly_summary_target_${this.selectedTarget}`);
      }
      if (this.selectedGoal === 'bounding_box') {
        return this.$t('models_and_train_configs.friendly_summary_boxes');
      }
      if (this.selectedGoal === 'ocr') {
        return this.$t('models_and_train_configs.friendly_summary_ocr');
      }
      if (this.selectedGoal === 'classification' && this.classificationStyle === 'arcface') {
        return this.$t('models_and_train_configs.friendly_summary_arcface');
      }
      if (this.selectedGoal === 'classification') {
        return this.$t('models_and_train_configs.friendly_summary_classify');
      }
      // Chip picked without going through goals
      const type = this.selectedModel && this.selectedModel.type;
      if (type === 'bounding_box') {
        return this.$t('models_and_train_configs.friendly_summary_boxes');
      }
      if (type === 'ocr') {
        return this.$t('models_and_train_configs.friendly_summary_ocr');
      }
      if (type === 'classification') {
        return this.$t('models_and_train_configs.friendly_summary_classify');
      }
      return this.$t('models_and_train_configs.friendly_summary_custom');
    },
    summaryHint() {
      if (this.classificationStyle === 'arcface') {
        return this.$t('models_and_train_configs.friendly_arcface_hint');
      }
      if (this.selectedGoal === 'bounding_box' && this.selectedTarget) {
        return this.$t(`models_and_train_configs.friendly_target_${this.selectedTarget}_hint`);
      }
      if (this.selectedGoal === 'bounding_box') {
        return this.$t('models_and_train_configs.friendly_target_pick_hint');
      }
      return '';
    },
  },
  watch: {
    selected_model_type: {
      immediate: true,
      handler(id) {
        this.syncGoalFromModelId(id);
      },
    },
    target_device: {
      immediate: true,
      handler(value) {
        if (value && BOXIFY_TARGET_PRESETS[value]) {
          this.selectedTarget = value;
        }
      },
    },
    model_buttons() {
      this.syncGoalFromModelId(this.selected_model_type);
    },
  },
  methods: {
    findModel(id) {
      return this.model_buttons.find((b) => b.id === id) || null;
    },
    emitSelection(model, options = {}) {
      if (!model || this.disabled) {
        return;
      }
      this.$emit('clicked', model);
      if (Object.keys(options).length > 0) {
        this.$emit('apply-options', options);
      }
    },
    selectGoal(goalId) {
      if (this.disabled) {
        return;
      }
      this.selectedGoal = goalId;
      this.classificationStyle = null;
      if (goalId !== 'bounding_box') {
        this.selectedTarget = null;
      }
      if (goalId === 'bounding_box') {
        this.applyRecommended(RECOMMENDED.bounding_box);
      } else if (goalId === 'ocr') {
        this.applyRecommended(RECOMMENDED.ocr);
      }
      // classification waits for style choice
    },
    selectTarget(targetId) {
      if (this.disabled || !BOXIFY_TARGET_PRESETS[targetId]) {
        return;
      }
      this.selectedGoal = 'bounding_box';
      this.selectedTarget = targetId;
      this.applyRecommended(RECOMMENDED.bounding_box, { ...BOXIFY_TARGET_PRESETS[targetId] });
    },
    selectClassificationStyle(style) {
      if (this.disabled) {
        return;
      }
      this.selectedGoal = 'classification';
      this.classificationStyle = style;
      if (style === 'arcface') {
        // Balanced batches hurt singleton-heavy catalogs (e.g. full Lego);
        // leave off by default — enable only for mid-tail print subsets.
        this.applyRecommended(RECOMMENDED.classification_arcface, {
          use_arcface: 'Yes',
          balanced_batches: 'No',
        });
      } else {
        this.applyRecommended(RECOMMENDED.classification_simple, {
          use_arcface: 'No',
          balanced_batches: 'No',
        });
      }
    },
    applyRecommended(modelId, options = {}) {
      const model = this.findModel(modelId);
      if (!model) {
        // Recommended model missing from catalog — open full list so user can pick.
        this.showAllModels = true;
        return;
      }
      this.emitSelection(model, options);
    },
    onChipClicked(model) {
      if (this.disabled) {
        return;
      }
      this.syncGoalFromModelId(model.id);
      // Chip path: do not force ArcFace; leave existing / defaults.
      this.$emit('clicked', model);
    },
    syncGoalFromModelId(id) {
      if (!id) {
        return;
      }
      const model = this.findModel(id);
      if (!model) {
        return;
      }
      this.selectedGoal = model.type || null;
      if (model.type !== 'classification') {
        this.classificationStyle = null;
      }
    },
  },
};
</script>

<style scoped>
.friendly-model-selector {
  width: 100%;
}

.step-question {
  font-weight: 600;
  margin: 0 0 12px;
  font-size: 15px;
}

.goal-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.goal-cards--narrow {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.goal-cards--targets {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.goal-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 6px;
  padding: 14px 14px 12px;
  border: 2px solid #d0d5dd;
  border-radius: 10px;
  background: #fafbfc;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  min-height: 112px;
}

.goal-card:hover:not(:disabled) {
  border-color: #90a4ae;
  background: #fff;
}

.goal-card.selected {
  border-color: var(--v-theme-primary, #1976d2);
  background: #e3f2fd;
  box-shadow: 0 0 0 1px var(--v-theme-primary, #1976d2);
}

.goal-card.disabled,
.goal-card:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.goal-icon {
  margin-bottom: 2px;
  color: #455a64;
}

.goal-title {
  font-weight: 700;
  font-size: 14px;
  line-height: 1.25;
  color: #1a1a1a;
}

.goal-example {
  font-size: 12px;
  line-height: 1.35;
  color: #546e7a;
}

.selector-step {
  margin-top: 4px;
  margin-bottom: 8px;
}

.selection-summary {
  margin: 4px 0 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
}

.summary-text {
  font-size: 14px;
  color: #1b5e20;
}

.summary-model {
  color: #2e7d32;
  font-weight: 500;
}

.summary-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #33691e;
}

.all-models-toggle {
  margin-bottom: 8px;
}

.linkish {
  background: none;
  border: none;
  padding: 0;
  color: #1565c0;
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
}

.linkish:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.all-models-help {
  font-size: 12px;
  color: #607d8b;
  margin: 0 0 8px;
}

@media (max-width: 900px) {
  .goal-cards {
    grid-template-columns: 1fr;
  }
  .goal-cards--narrow {
    grid-template-columns: 1fr;
  }
}
</style>
